import Alert from "@mui/material/Alert";
import {useScoutingIndices} from "@/utils/scoutingStatisticsData";
import {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TuneIcon from "@mui/icons-material/Tune";
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

import IndexCard from "@/components/IndexCard";
import MainActions from "@/components/MainActions";
import VLFSelectNew from "@/components/Input/SelectNew";
import {computeIndex, indexHistory, indicesFor} from "@/utils/scoutingIndices";
import {
	competitionOptions,
	filterMatches,
	per90,
	ratio,
	seasonRows,
	sumMatches,
} from "@/utils/wyscoutImport";

import "./index.scss";
import {COLORS} from "@/style/utils";

const num = (value, digits = 0) =>
	value === null || value === undefined || !Number.isFinite(value)
		? "—"
		: value.toLocaleString("it-IT", {
				minimumFractionDigits: digits,
				maximumFractionDigits: digits,
			});

const OUTFIELD_COLUMNS = [
	{key: "goals", label: "Gol", digits: 0},
	{key: "xg", label: "xG", digits: 2},
	{key: "assists", label: "Assist", digits: 0},
	{key: "xa", label: "xA", digits: 2},
	{key: "shots", label: "Tiri", digits: 0},
];

const GOALKEEPER_COLUMNS = [
	{key: "goalsConceded", label: "Gol subiti", digits: 0},
	{key: "xcg", label: "xCG", digits: 2},
	{key: "saves", label: "Parate", digits: 0},
	{key: "shotsAgainst", label: "Tiri subiti", digits: 0},
];

function Kpi({label, value, sub}) {
	return (
		<div className="pms-kpi">
			<span className="pms-kpi__label">{label}</span>
			<span className="pms-kpi__value">{value}</span>
			{sub && <span className="pms-kpi__sub">{sub}</span>}
		</div>
	);
}

// Tab Statistiche della scheda scouting, dalle partite importate da Wyscout.
export default function PlayerMatchStats({data, goalkeeper, attitudes = []}) {
	const navigate = useNavigate();
	const indexState = useScoutingIndices();
	const configuredIndices =
		indexState.status === "ready"
			? (indexState.data.indices || []).filter((i) => i.active)
			: [];
	const matches = useMemo(() => data?.matches || [], [data]);
	const [officialOnly, setOfficialOnly] = useState(true);
	const [perNinety, setPerNinety] = useState(false);
	const rows = useMemo(
		() => seasonRows(matches, {officialOnly}),
		[matches, officialOnly],
	);
	const [season, setSeason] = useState(() => rows[0]?.season || null);
	const [competition, setCompetition] = useState(null);
	const currentSeason = rows.some((r) => r.season === season)
		? season
		: rows[0]?.season || null;

	const competitions = competitionOptions(
		filterMatches(matches, {officialOnly}),
		currentSeason,
	);
	const selected = filterMatches(matches, {
		season: currentSeason,
		competition,
		officialOnly,
	});
	const totals = sumMatches(selected);
	const columns = goalkeeper ? GOALKEEPER_COLUMNS : OUTFIELD_COLUMNS;
	const seasons = rows.map((r) => r.season);
	const officialMatches = filterMatches(matches, {officialOnly});
	const indices = indicesFor(goalkeeper, configuredIndices).map((index) => ({
		index: computeIndex(index, totals),
		history: indexHistory(index, officialMatches, seasons),
	}));
	const minutesTrend = rows
		.slice()
		.reverse()
		.map((r) => ({season: r.season, minutes: r.totals.minutes}));

	const cell = (totalsRow, column) =>
		num(
			perNinety
				? per90(totalsRow[column.key], totalsRow.minutes)
				: totalsRow[column.key],
			perNinety ? 2 : column.digits,
		);

	return (
		<div className="player-match-stats">
			{indexState.status === "error" && (
				<Alert severity="error">
					Indici non disponibili.{" "}
					<button onClick={() => indexState.refresh().catch(() => {})}>
						Riprova indici
					</button>
				</Alert>
			)}
			{(indexState.status === "idle" || indexState.status === "loading") && (
				<p>Caricamento indici condivisi…</p>
			)}
			{indexState.status === "ready" && !configuredIndices.length && (
				<Alert severity="info">Nessun indice attivo configurato.</Alert>
			)}
			<div className="pms-filters">
				<div>
					<VLFSelectNew
						label="Stagione"
						options={rows.map((r) => ({value: r.season, label: r.season}))}
						value={
							currentSeason
								? {value: currentSeason, label: currentSeason}
								: null
						}
						onChange={(o) => {
							if (!o) return;
							setSeason(o.value);
							setCompetition(null);
						}}
						sorted={false}
						isClearable={false}
						noMargin
					/>
				</div>
				<div>
					<VLFSelectNew
						label="Competizione"
						placeholder="Tutte"
						options={competitions.map((c) => ({
							value: c.competition,
							label: c.competition + " · " + num(c.minutes) + "'",
						}))}
						value={
							competition
								? {
										value: competition,
										label:
											competition +
											" · " +
											num(
												competitions.find((c) => c.competition === competition)
													?.minutes,
											) +
											"'",
									}
								: null
						}
						onChange={(o) => setCompetition(o?.value || null)}
						sorted={false}
						noMargin
					/>
				</div>
				<FormControlLabel
					className="pms-check"
					control={
						<Checkbox
							size="small"
							checked={officialOnly}
							onChange={(e) => setOfficialOnly(e.target.checked)}
						/>
					}
					label="Solo ufficiali"
				/>
			</div>

			<div className="pms-kpis">
				<Kpi label="Gare" value={num(totals.matches)} />
				<Kpi
					label="Minuti"
					value={num(totals.minutes)}
					sub={
						totals.matches
							? num(totals.minutes / totals.matches) + "' a gara"
							: null
					}
				/>
				{goalkeeper ? (
					<>
						<Kpi
							label="Gol subiti"
							value={num(totals.goalsConceded)}
							sub={
								num(per90(totals.goalsConceded, totals.minutes), 2) + " /90'"
							}
						/>
						<Kpi label="xCG" value={num(totals.xcg, 2)} />
						<Kpi
							label="Parate"
							value={
								ratio(totals.saves, totals.shotsAgainst) === null
									? "—"
									: num(ratio(totals.saves, totals.shotsAgainst), 1) + "%"
							}
						/>
					</>
				) : (
					<>
						<Kpi
							label="Gol"
							value={num(totals.goals)}
							sub={num(per90(totals.goals, totals.minutes), 2) + " /90'"}
						/>
						<Kpi
							label="xG"
							value={num(totals.xg, 2)}
							sub={num(per90(totals.xg, totals.minutes), 2) + " /90'"}
						/>
						<Kpi
							label="Gol − xG"
							value={num((totals.goals || 0) - (totals.xg || 0), 2)}
						/>
						<Kpi
							label="Assist"
							value={num(totals.assists)}
							sub={"xA " + num(totals.xa, 2)}
						/>
					</>
				)}
			</div>

			<section className="pms-section">
				<div className="pms-section__head">
					<h3>Indici · {currentSeason}</h3>
					<MainActions
						actions={[
							{
								icon: <TuneIcon />,
								label: "Configura indici",
								title: "Configura indici",
								onClick: () => navigate("/scouting/indici"),
							},
						]}
					/>
				</div>
				<div className="pms-indices">
					{indices.map(({index, history}) => (
						<IndexCard
							key={index.id}
							index={index}
							history={history}
							marked={attitudes.includes(index.attitude)}
						/>
					))}
				</div>
			</section>

			<section className="pms-section">
				<div className="pms-section__head">
					<h3>Per stagione</h3>
					<ButtonGroup disableElevation size="small" aria-label="Valori">
						{[
							[false, "Totali"],
							[true, "Per 90'"],
						].map(([value, label]) => (
							<Button
								key={label}
								aria-pressed={perNinety === value}
								variant={perNinety === value ? "contained" : "outlined"}
								onClick={() => setPerNinety(value)}
							>
								{label}
							</Button>
						))}
					</ButtonGroup>
				</div>
				<div className="pms-table-wrap">
					<table className="pms-table">
						<thead>
							<tr>
								<th>Stagione</th>
								<th>Competizioni</th>
								<th>Gare</th>
								<th>Minuti</th>
								{columns.map((c) => (
									<th key={c.key}>{c.label}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{rows.map((r) => (
								<tr
									key={r.season}
									className={r.season === currentSeason ? "is-selected" : ""}
									onClick={() => {
										setSeason(r.season);
										setCompetition(null);
									}}
								>
									<td>{r.season}</td>
									<td className="pms-table__competitions">
										{r.competitions.join(", ")}
									</td>
									<td>{num(r.totals.matches)}</td>
									<td>{num(r.totals.minutes)}</td>
									{columns.map((c) => (
										<td key={c.key}>{cell(r.totals, c)}</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</section>

			{minutesTrend.length > 1 && (
				<section className="pms-section">
					<h3>Minuti per stagione</h3>
					<div className="pms-chart">
						<ResponsiveContainer width="100%" height={180}>
							<BarChart
								data={minutesTrend}
								margin={{top: 8, right: 8, bottom: 0, left: -12}}
							>
								<CartesianGrid vertical={false} stroke={COLORS.border} />
								<XAxis
									dataKey="season"
									tick={{fontSize: 11, fill: COLORS.textSecondary}}
									axisLine={false}
									tickLine={false}
									interval="preserveStartEnd"
								/>
								<YAxis
									tick={{fontSize: 11, fill: COLORS.textSecondary}}
									axisLine={false}
									tickLine={false}
								/>
								<Tooltip
									cursor={{fill: "rgba(15, 23, 42, 0.05)"}}
									formatter={(value) => [num(value) + "'", "Minuti"]}
								/>
								<Bar
									dataKey="minutes"
									fill={COLORS.primary}
									radius={[4, 4, 0, 0]}
									maxBarSize={28}
								/>
							</BarChart>
						</ResponsiveContainer>
					</div>
				</section>
			)}

			<section className="pms-section">
				<h3>
					Partite · {currentSeason}
					{competition ? " · " + competition : ""}
				</h3>
				<div className="pms-table-wrap">
					<table className="pms-table pms-table--matches">
						<thead>
							<tr>
								<th>Data</th>
								<th>Partita</th>
								<th>Ruolo</th>
								<th>Min</th>
								{columns.map((c) => (
									<th key={c.key}>{c.label}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{selected.map((m) => (
								<tr key={m.date + m.name}>
									<td>{m.date.split("-").reverse().join("/")}</td>
									<td className="pms-table__match">
										{m.name}
										<small>{m.competition}</small>
									</td>
									<td>{m.roles.join(", ") || m.positions.join(", ") || "—"}</td>
									<td>{num(m.values.minutes)}</td>
									{columns.map((c) => (
										<td key={c.key}>{num(m.values[c.key], c.digits)}</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</section>
		</div>
	);
}
